import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface Folder {
  id: string;
  name: string;
  parent_id: string | null;
}

interface CreateFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  folders: Folder[];
  onFolderCreated: () => void;
}

export default function CreateFolderDialog({
  open,
  onOpenChange,
  companyId,
  folders,
  onFolderCreated,
}: CreateFolderDialogProps) {
  const [folderName, setFolderName] = useState('');
  const [parentFolderId, setParentFolderId] = useState<string>('');
  const [visibility, setVisibility] = useState<string>('INTERNAL');
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const buildFolderPath = (folderId: string): string => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return '';
    if (!folder.parent_id) return folder.name;
    return `${buildFolderPath(folder.parent_id)} / ${folder.name}`;
  };

  const handleCreate = async () => {
    if (!folderName.trim()) {
      toast({
        title: 'Folder name required',
        description: 'Please enter a folder name',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('Not authenticated');
      }

      const { error } = await supabase.from('data_room_folders').insert({
        company_id: companyId,
        name: folderName.trim(),
        parent_id: parentFolderId === 'root' || !parentFolderId ? null : parentFolderId,
        visibility: visibility,
        created_by: userData.user.id,
      });

      if (error) throw error;

      toast({
        title: 'Folder created',
        description: `"${folderName}" has been created successfully`,
      });

      setFolderName('');
      setParentFolderId('');
      setVisibility('INTERNAL');
      onOpenChange(false);
      onFolderCreated();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast({
        title: 'Failed to create folder',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Folder</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              placeholder="Enter folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              disabled={creating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="parent-folder">Parent Folder (Optional)</Label>
            <Select value={parentFolderId} onValueChange={setParentFolderId} disabled={creating}>
              <SelectTrigger id="parent-folder">
                <SelectValue placeholder="Select parent folder (root if none)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="root">Root (No Parent)</SelectItem>
                {folders
                  .map((folder) => ({
                    ...folder,
                    path: buildFolderPath(folder.id),
                  }))
                  .sort((a, b) => a.path.localeCompare(b.path))
                  .map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.path}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="visibility">Visibility</Label>
            <Select value={visibility} onValueChange={setVisibility} disabled={creating}>
              <SelectTrigger id="visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INTERNAL">Internal Only</SelectItem>
                <SelectItem value="INVESTOR">Visible to Investors</SelectItem>
                <SelectItem value="PUBLIC">Public</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
